/**
 * 마이페이지
 * 프로필, 인증코드 관리, 요금제·구독 관리
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Snackbar,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyIcon from '@mui/icons-material/Key';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useAuth } from '@/contexts/AuthContext';
import { subscriptionAPI } from '../services/api';
import axios from 'axios';

export default function MyPage() {
  const { user, isAdmin, isPaid, refreshUser } = useAuth();
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [authCode, setAuthCode] = useState('');
  const [showAuthCode, setShowAuthCode] = useState(false);
  const [isLoadingCode, setIsLoadingCode] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [subStatus, setSubStatus] = useState(null);

  const loadSubscriptionStatus = async () => {
    try {
      const response = await subscriptionAPI.getStatus();
      if (response.data?.success) {
        setSubStatus(response.data.data);
      }
    } catch {
      // 로그인 상태면 실패해도 기본 user 정보로 표시
    }
  };

  useEffect(() => {
    if (user) {
      fetchAuthCode();
      loadSubscriptionStatus();
    }
  }, [user]);

  const fetchAuthCode = async () => {
    try {
      const response = await axios.get('/api/auth/my-code');
      if (response.data.success) {
        setAuthCode(response.data.data.authCode || '');
      }
    } catch {
      // 인증코드가 없을 수 있음
    }
  };

  const handleUpgrade = async () => {
    setUpgradeLoading(true);
    try {
      const response = await subscriptionAPI.checkout();
      if (response.data.success && response.data.data.checkoutUrl) {
        window.location.href = response.data.data.checkoutUrl;
      } else {
        setSnackbar({ open: true, message: response.data.error || '결제 세션 생성에 실패했습니다.', severity: 'error' });
      }
    } catch (err) {
      const msg = err.response?.data?.error || '결제 세션 생성에 실패했습니다.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setUpgradeLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const response = await subscriptionAPI.portal();
      if (response.data.success && response.data.data.portalUrl) {
        window.location.href = response.data.data.portalUrl;
      } else {
        setSnackbar({
          open: true,
          message: response.data.error || '구독 관리 페이지를 열지 못했습니다.',
          severity: 'error',
        });
      }
    } catch (err) {
      const msg = err.response?.data?.error || '구독 관리 페이지를 열지 못했습니다.';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setPortalLoading(false);
    }
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(authCode);
      setSnackbar({ open: true, message: '인증코드가 복사되었습니다.', severity: 'success' });
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = authCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setSnackbar({ open: true, message: '인증코드가 복사되었습니다.', severity: 'success' });
    }
  };

  const handleReissueCode = async () => {
    setIsLoadingCode(true);
    try {
      const response = await axios.post('/api/auth/reissue-code');
      if (response.data.success) {
        setAuthCode(response.data.data.authCode);
        setShowAuthCode(true);
        setSnackbar({ open: true, message: '새 인증코드가 발급되었습니다.', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: response.data.error, severity: 'error' });
      }
    } catch {
      setSnackbar({ open: true, message: '인증코드 재발급에 실패했습니다.', severity: 'error' });
    }
    setIsLoadingCode(false);
  };

  // 인증코드 마스킹 (앞 8자 + ••• + 뒤 8자)
  const maskedCode = authCode
    ? `${authCode.slice(0, 8)}${'•'.repeat(30)}${authCode.slice(-8)}`
    : '인증코드 없음';

  // plan(free|paid) + admin + 보상 이용권 표시
  const getPlanInfo = () => {
    if (isAdmin || user?.isAdmin || user?.role === 'admin' || subStatus?.isAdmin) {
      return {
        name: '플랫폼 관리자',
        color: '#6a1b9a',
        bgColor: '#f3e5f5',
        isAdmin: true,
        isPaid: true,
        showBillingPortal: false,
      };
    }

    const paid =
      subStatus?.isPaid
      ?? (user?.plan === 'paid' || isPaid || (user?.accessLevel && user.accessLevel !== 'Level1'));

    if (!paid) {
      return {
        name: '무료',
        color: '#757575',
        bgColor: '#f5f5f5',
        isPaid: false,
        showBillingPortal: false,
      };
    }

    return {
      name: subStatus?.planName || user?.planName || '스탠다드',
      color: '#1976d2',
      bgColor: '#e3f2fd',
      isPaid: true,
      showBillingPortal: !!(subStatus?.showBillingPortal ?? user?.showBillingPortal),
      planExpiresAt: subStatus?.planExpiresAt || user?.planExpiresAt,
      planLabel: subStatus?.planLabel || user?.planLabel,
      planSource: subStatus?.planSource || user?.planSource,
    };
  };

  const planInfo = getPlanInfo();

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        마이페이지
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        요금제·구독과 로그인 인증코드를 관리합니다.
      </Typography>

      {/* 요금제 / 구독 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CreditCardIcon sx={{ color: '#555' }} />
          <Typography variant="h6" fontWeight="bold">
            요금제 · 구독
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="body1" color="text.secondary">이메일</Typography>
          <Typography variant="body1">{user?.email}</Typography>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body1" color="text.secondary">현재 요금제</Typography>
          <Chip
            label={planInfo.name}
            size="small"
            sx={{
              backgroundColor: planInfo.bgColor,
              color: planInfo.color,
              fontWeight: 600,
              fontSize: '0.85rem',
            }}
          />
        </Box>

        {!planInfo.isAdmin && !planInfo.isPaid && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              무료 플랜은 하루 질문 30회로 제한됩니다. 스탠다드로 업그레이드하면 무제한으로 이용할 수 있습니다.
            </Alert>
            <Button
              variant="contained"
              onClick={handleUpgrade}
              disabled={upgradeLoading}
              startIcon={upgradeLoading ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {upgradeLoading ? '이동 중...' : '스탠다드 업그레이드'}
            </Button>
          </>
        )}

        {!planInfo.isAdmin && planInfo.isPaid && planInfo.showBillingPortal && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              결제 수단 변경, 영수증 확인, <strong>구독 해지</strong>는 Polar 고객 포털에서 진행합니다.
              해지 후 웹훅으로 KOAI 요금제가 자동으로 무료로 전환됩니다.
            </Alert>
            <Button
              variant="contained"
              onClick={handleManageSubscription}
              disabled={portalLoading}
              endIcon={portalLoading ? <CircularProgress size={16} color="inherit" /> : <OpenInNewIcon />}
            >
              {portalLoading ? '포털 여는 중...' : '구독 관리 (해지·결제수단)'}
            </Button>
            <Button
              size="small"
              sx={{ ml: 1 }}
              onClick={async () => {
                await refreshUser?.();
                await loadSubscriptionStatus();
                setSnackbar({ open: true, message: '요금제 정보를 새로고침했습니다.', severity: 'success' });
              }}
            >
              상태 새로고침
            </Button>
          </>
        )}

        {!planInfo.isAdmin && planInfo.isPaid && !planInfo.showBillingPortal && (
          <>
            <Alert severity="success" sx={{ mb: 2 }}>
              관리자가 부여한 이용권입니다
              {planInfo.planLabel ? ` (${planInfo.planLabel})` : ''}.
              Polar 결제·해지 대상이 아니며, 만료일까지 스탠다드와 동일하게 이용할 수 있습니다.
            </Alert>
            <Button
              size="small"
              onClick={async () => {
                await refreshUser?.();
                await loadSubscriptionStatus();
                setSnackbar({ open: true, message: '요금제 정보를 새로고침했습니다.', severity: 'success' });
              }}
            >
              상태 새로고침
            </Button>
          </>
        )}

        {planInfo.isAdmin && (
          <Alert severity="info">
            관리자 계정은 Polar 구독과 무관하게 모든 기능을 사용할 수 있습니다.
          </Alert>
        )}
      </Paper>

      {/* 인증코드 정보 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <KeyIcon sx={{ color: '#555' }} />
          <Typography variant="h6" fontWeight="bold">
            인증코드 정보
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography variant="body1" color="text.secondary">인증코드 상태</Typography>
          <Chip
            label={authCode ? '발급됨' : '미발급'}
            size="small"
            color={authCode ? 'success' : 'default'}
            sx={{ fontSize: '0.85rem' }}
          />
        </Box>
      </Paper>

      {/* 현재 인증코드 */}
      <Paper sx={{ p: 3, mb: 3, backgroundColor: '#fffde7' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography sx={{ fontSize: '1.2rem' }}>🔑</Typography>
          <Typography variant="h6" fontWeight="bold">
            현재 인증코드
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 2,
            backgroundColor: '#fff',
            borderRadius: 1,
            border: '1px solid #e0e0e0',
            mb: 1,
          }}
        >
          <Typography
            variant="body1"
            sx={{
              flex: 1,
              fontFamily: 'monospace',
              wordBreak: 'break-all',
              fontSize: '0.95rem',
            }}
          >
            {showAuthCode ? authCode : maskedCode}
          </Typography>
          <Tooltip title={showAuthCode ? '숨기기' : '보기'}>
            <IconButton size="small" onClick={() => setShowAuthCode(!showAuthCode)}>
              {showAuthCode ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Tooltip title="복사">
            <IconButton size="small" onClick={handleCopyCode} disabled={!authCode}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <WarningAmberIcon sx={{ fontSize: 18, color: '#f57c00' }} />
          <Typography variant="body2" sx={{ color: '#f57c00' }}>
            인증코드는 로그인에 사용됩니다. 타인에게 공유하지 마세요!
          </Typography>
        </Box>
      </Paper>

      {/* 인증코드 재발급 */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <RefreshIcon sx={{ color: '#555' }} />
          <Typography variant="h6" fontWeight="bold">
            인증코드 재발급
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }} icon={<InfoOutlinedIcon />}>
          새 인증코드를 발급하면 기존 인증코드는 무효화됩니다. 새 인증코드로만 로그인할 수 있습니다.
        </Alert>

        <Button
          variant="contained"
          startIcon={<RefreshIcon />}
          onClick={handleReissueCode}
          disabled={isLoadingCode}
          color="primary"
        >
          {isLoadingCode ? '발급 중...' : '인증코드 재발급'}
        </Button>
      </Paper>

      {/* 인증코드 사용 방법 */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography sx={{ fontSize: '1.2rem' }}>📋</Typography>
          <Typography variant="h6" fontWeight="bold">
            인증코드 사용 방법
          </Typography>
        </Box>

        <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
          <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
            인증코드를 복사합니다.
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
            로그인 페이지에서 &quot;인증코드 로그인&quot;을 선택합니다.
          </Typography>
          <Typography component="li" variant="body1" sx={{ mb: 0.5 }}>
            128자 인증코드를 붙여넣고 &quot;시작하기&quot;를 클릭합니다.
          </Typography>
        </Box>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
